import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CompanyFirstLook, FirstLookLine, FirstLookHeading } from '@/components/shared/DealCard';
import { getStartupFirstLook } from '@/lib/api';

// R06 (20 Oct 2026): the CEO opens a company's saved first look AFTER its card has left his inbox, with the call material added for
// each booked call. Display only: the backend composes every line (`first_look_task.read_company_first_look`); nothing is inferred or
// filled here, so a missing fact stays visibly missing and a call whose material could not be built says so.
export default function StartupFirstLookDialog({ startupId, name, open, onClose }) {
    const [state, setState] = useState({ status: 'idle', view: null });

    useEffect(() => {
        if (!open || !startupId) return undefined;
        let cancelled = false;
        setState({ status: 'loading', view: null });
        getStartupFirstLook(startupId)
            .then((view) => { if (!cancelled) setState({ status: 'ready', view }); })
            .catch(() => { if (!cancelled) setState({ status: 'error', view: null }); });
        return () => { cancelled = true; };
    }, [open, startupId]);

    const calls = state.view && state.view.call_depth && Array.isArray(state.view.call_depth.calls) ? state.view.call_depth.calls : [];
    return (
        <Dialog open={!!open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="startup-first-look-dialog">
                <DialogHeader>
                    <DialogTitle>{`First look: ${name || 'this company'}`}</DialogTitle>
                    <DialogDescription>What was saved about this company, and what was added for each booked call.</DialogDescription>
                </DialogHeader>
                {state.status === 'loading' && <p data-testid="startup-first-look-loading">Loading the saved first look...</p>}
                {state.status === 'error' && (
                    <p data-testid="startup-first-look-error">The saved first look could not be loaded. Nothing is shown in its place.</p>
                )}
                {state.status === 'ready' && state.view && (
                    <div data-testid="startup-first-look-ready">
                        <CompanyFirstLook firstLook={state.view.first_look} />
                        <section data-testid="startup-first-look-calls">
                            <FirstLookHeading>{(state.view.call_depth && state.view.call_depth.heading) || 'Before the call'}</FirstLookHeading>
                            {calls.length === 0 && <p data-testid="startup-first-look-no-calls">No call is booked for this company yet.</p>}
                            {calls.map((call) => (
                                <div key={call.event_id} className="mb-4" data-testid={`startup-first-look-call-${call.event_id}`}>
                                    <p style={{ fontWeight: 700 }}>{call.heading}</p>
                                    {(call.lines || []).map((line, i) => (
                                        <FirstLookLine key={`${call.event_id}-${i}`} line={line} testIdPrefix="startup-first-look-call-line" />
                                    ))}
                                </div>
                            ))}
                        </section>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
